import { defineStore } from "pinia";
import { DocumentsApi, SessionsApi } from "@simbelapp/order-sdk";
import type { ISessionsDetailsDocumentsStore } from "~/utils/interfaces/sessions-interfaces";

import { useFeedback } from "~/composables/feedback/useFeedback";
import { ItemType, MicroserviceEnum } from "~/utils/enums/common.enums";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { usePagination } from "~/composables/paginations/usePagination";
import { PaginationKeysEnum } from "~/utils/enums/pagination-keys.enums";

export const useSessionDetailsDocumentsStore = defineStore("sessionDetailsDocumentsStore", {
  state: (): ISessionsDetailsDocumentsStore => {
    return {
      showSessionRemoveDocumentModal: false,
      sessionDocumentsPagination: null,
      fileUpload: {
        showFileUpload: false,
      },
    };
  },
  actions: {
    // Update session comment
    async updateSessionComment(itemId: string, type: ItemType, comment: string) {
      const feedback = useFeedback();

      try {
        if (itemId) {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionsApi);
          await apiInstance.updateSessionComment({ sessionId: itemId, updateSessionCommentDto: { comment } });
        }
      } catch {
        feedback.error(`erreur`, "small");
      }
    },

    async removeSessionDocument(documentId: string) {
      const feedback = useFeedback();

      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, DocumentsApi);
      try {
        await apiInstance.removeDocument({ documentId });
        feedback.success("Document supprimé", "small");
      } catch {
        feedback.error(`Une erreur est survenue lors de la suppression`, "small");
      }
    },

    async fetchSessionDocumentsWithPagination(id: string, options?: { limit?: number }) {
      const pagination = usePagination(PaginationKeysEnum.SESSION_DOCUMENTS_PAGINATION);
      const feedback = useFeedback();

      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, DocumentsApi);
      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
      const transformQuery = Object.fromEntries(new URLSearchParams(paginateOptions).entries());
      if (options?.limit != null) {
        transformQuery.limit = String(options.limit);
        transformQuery.offset = "0";
      }

      try {
        const result = await apiInstance.getSessionDocuments({
          sessionId: id,
          ...transformQuery,
        });

        this.sessionDocumentsPagination = result;
      } catch {
        feedback.error(`Une erreur est survenue lors de la récupération des documents`, "small");
      }
    },
    async fetchMySessionDocumentsWithPagination(id: string, options?: { limit?: number }) {
      const pagination = usePagination(PaginationKeysEnum.SESSION_DOCUMENTS_PAGINATION);
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, DocumentsApi);
      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
      const transformQuery = Object.fromEntries(new URLSearchParams(paginateOptions).entries());
      if (options?.limit != null) {
        transformQuery.limit = String(options.limit);
        transformQuery.offset = "0";
      }

      try {
        const result = await apiInstance.getMySessionDocuments({
          sessionId: id,
          ...transformQuery,
        });

        this.sessionDocumentsPagination = result;
      } catch {
        feedback.error(`Une erreur est survenue lors de la récupération des documents`, "small");
      }
    },
  },
});
